import { computed, ref } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import { z } from 'zod'

import { usePresentationTranslator } from '~/composables/usePresentationTranslator'
import type { FieldHandle } from '~/interfaces/FieldHandle'
import { useTanstackForm } from '~/composables/useTanstackForm'
import type { FormProblemState } from '~/services/forms/FormProblemState'
import type { StepUpCredential } from '~/types/StepUpCredential'

interface StepUpFormValues {
  code: string
  recoveryCode: string
}

const hasCompleteCode = (code: string): boolean => /^\d{6,}$/.test(code.trim())
const hasCompleteRecoveryCode = (recoveryCode: string): boolean => recoveryCode.trim().length > 5

interface UseStepUpCredentialFormReturn {
  codeField: FieldHandle<string>
  getFieldError: (field: keyof StepUpFormValues) => string
  handleSubmit: () => Promise<void>
  isValid: ComputedRef<boolean>
  onCodeUpdate: (value: string) => void
  onRecoveryUpdate: (value: string) => void
  problem: ComputedRef<FormProblemState | null>
  recoveryCodeField: FieldHandle<string>
  resetCredentials: (recoveryMode: boolean) => void
  useRecovery: Ref<boolean>
}

/** The step-up one-time credential form; credentials never reach draft storage. */
export function useStepUpCredentialForm(
  submit: (credential: StepUpCredential) => Promise<void>
): UseStepUpCredentialFormReturn {
  const t = usePresentationTranslator()
  const useRecovery = ref(false)

  const stepUpFormSchema = z
    .object({ code: z.string(), recoveryCode: z.string() })
    .superRefine((values, context) => {
      if (!useRecovery.value && !hasCompleteCode(values.code)) {
        context.addIssue({
          code: 'custom',
          message: t('auth.mfaCode.incompleteCode'),
          path: ['code']
        })
      }
      if (useRecovery.value && !hasCompleteRecoveryCode(values.recoveryCode)) {
        context.addIssue({
          code: 'custom',
          message: t('auth.mfaCode.incompleteRecoveryCode'),
          path: ['recoveryCode']
        })
      }
    })

  const { fields, getFieldError, clearFieldError, handleSubmit, problem, form } =
    useTanstackForm<StepUpFormValues>({
      defaultValues: { code: '', recoveryCode: '' },
      formId: 'step-up',
      onSubmit: async (value) => {
        await submit(
          useRecovery.value ? { recovery_code: value.recoveryCode } : { code: value.code }
        )
        // A failed attempt keeps the modal open; clear the codes so a retry starts clean.
        form.reset()
      },
      schema: stepUpFormSchema
    })

  const codeField = fields.code
  const recoveryCodeField = fields.recoveryCode

  const isValid = computed(() =>
    useRecovery.value
      ? hasCompleteRecoveryCode(recoveryCodeField.state.value)
      : codeField.state.value.trim().length >= 6
  )

  const onCodeUpdate = (value: string) => {
    codeField.api.handleChange(value)
    clearFieldError('code')
  }

  const onRecoveryUpdate = (value: string) => {
    recoveryCodeField.api.handleChange(value)
    clearFieldError('recoveryCode')
  }

  /** Clears both codes and sets which credential the form collects. */
  const resetCredentials = (recoveryMode: boolean) => {
    useRecovery.value = recoveryMode
    form.reset()
  }

  return {
    codeField,
    getFieldError,
    handleSubmit,
    isValid,
    onCodeUpdate,
    onRecoveryUpdate,
    problem,
    recoveryCodeField,
    resetCredentials,
    useRecovery
  }
}
